import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Clock, Flame, PartyPopper, Play, Target } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '@/lib/api';

const TONE = {
  green: 'from-indigo-50 to-violet-50 border-indigo-100',
  orange: 'from-amber-50 to-orange-50 border-amber-200',
  red: 'from-rose-50 to-red-50 border-rose-200',
};

/** The card the student opens each morning. Hidden when there's no plan. */
export function TodaysMissionCard({ onStart }) {
  const [data, setData] = useState(null);
  const [streak, setStreak] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    apiGet('/premium/v1/missions/today').then(setData).catch(() => setData(null));
    apiGet('/premium/v1/missions/streak').then(setStreak).catch(() => setStreak(null));
  }, []);

  const toggle = async (planId, ref, done) => {
    try {
      const res = await apiPost(`/premium/v1/plans/${planId}/items/done`, { ref, done });
      if (res.streak?.awarded) {
        toast.success(`${res.streak.current} days in a row — ${res.streak.awarded} Veda Coins are yours!`);
      }
      const [fresh, st] = await Promise.all([
        apiGet('/premium/v1/missions/today'),
        apiGet('/premium/v1/missions/streak').catch(() => null),
      ]);
      setData(fresh); if (st) setStreak(st);
    } catch { /* the card stays as it is */ }
  };

  if (!data) return null;

  // Meta always shows: a rest day still deserves the countdown and the streak.
  if (!(data.missions || []).length) {
    if (!streak?.current && !data.nearest_exam) return null;
    return (
      <div className="rounded-3xl border border-orange-200 bg-orange-50/70 p-3" data-testid="mission-rest-day">
        {data.nearest_exam && (
          <p className="font-heading text-[13px] font-semibold text-slate-800" data-testid="mission-nearest-exam">
            {data.nearest_exam.exam_name} · {data.nearest_exam.days_remaining} day
            {data.nearest_exam.days_remaining === 1 ? '' : 's'} to go
          </p>
        )}
        {streak?.current > 0 && (
          <p className="font-heading text-[12px] text-orange-700 flex items-center gap-1.5 mt-0.5" data-testid="mission-streak">
            <Flame className="w-3.5 h-3.5 text-orange-500" /> {streak.current}-day streak alive
          </p>
        )}
        <p className="text-[11px] text-slate-600 font-body mt-0.5">
          Nothing scheduled today{data.is_holiday ? ' — enjoy the holiday' : ''}.
          {streak?.next_milestone ? ` ${streak.next_milestone - streak.current} more day(s) in a row earns ${streak.next_reward} Veda Coins.` : ''}
        </p>
        <button data-testid="mission-open-planner-empty" onClick={() => navigate('/planner')}
          className="mt-2 text-[11px] font-heading text-indigo-600 hover:underline">
          Open the full plan →
        </button>
        <button data-testid="mission-open-cockpit-empty" onClick={() => navigate('/cockpit')}
          className="mt-2 ml-3 text-[11px] font-heading text-fuchsia-600 hover:underline">
          Open today's cockpit →
        </button>
      </div>
    );
  }

  return (
    <div className={`rounded-3xl border bg-gradient-to-br p-4 ${TONE[data.risk] || TONE.green}`} data-testid="todays-mission-card">
      <div className="flex items-center justify-between gap-2">
        <p className="font-heading text-sm font-bold text-slate-900 flex items-center gap-1.5">
          <Target className="w-4 h-4 text-indigo-500" /> Today's mission
        </p>
        <div className="flex items-center gap-2">
          {streak?.current > 0 && (
            <span className="text-[11px] font-heading text-orange-600 flex items-center gap-1" data-testid="mission-streak">
              <Flame className="w-3 h-3" /> {streak.current}-day streak
            </span>
          )}
          <span className="text-[11px] font-body text-slate-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {data.total_minutes} min
          </span>
        </div>
      </div>
      {data.nearest_exam && (
        <p className="text-[11px] text-slate-600 font-body mt-0.5" data-testid="mission-nearest-exam">
          {data.nearest_exam.exam_name} · {data.nearest_exam.days_remaining} day
          {data.nearest_exam.days_remaining === 1 ? '' : 's'} to go
          {data.missions[0] ? ` · ${data.missions[0].progress_pct}% of the plan done` : ''}
        </p>
      )}
      {streak?.next_milestone && (
        <p className="text-[10px] text-slate-500 font-body mt-0.5" data-testid="mission-streak-next">
          {streak.next_milestone - streak.current} more day{streak.next_milestone - streak.current === 1 ? '' : 's'} in a row
          → {streak.next_reward} Veda Coins
        </p>
      )}
      {data.is_holiday && (
        <p className="text-[11px] text-amber-700 font-body mt-1 flex items-center gap-1" data-testid="mission-holiday-note">
          <PartyPopper className="w-3 h-3" /> Today is marked as a holiday — do this only if you feel like it.
        </p>
      )}

      {data.missions.map(m => (
        <div key={m.plan_id} className="mt-2.5" data-testid={`mission-${m.exam_id}`}>
          <p className="text-[11px] font-heading uppercase tracking-widest text-slate-400">
            {m.exam_name}{m.kind === 'revision' ? ' · revision day' : ''}
          </p>
          <div className="mt-1 space-y-1.5">
            {m.items.map(item => (
              <div key={item.ref} data-testid={`mission-item-${item.ref}`}>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggle(m.plan_id, item.ref, !item.done)}
                    data-testid={`mission-item-done-${item.ref}`}
                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${item.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 bg-white'}`}>
                    {item.done && <Check className="w-3 h-3" />}
                  </button>
                  <p className={`text-[12px] font-body min-w-0 flex-1 truncate ${item.done ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                    <span className="text-slate-400">{item.subject} · </span>{item.label}
                  </p>
                  <span className="text-[10px] font-body text-slate-400 flex-shrink-0">{item.minutes}m</span>
                </div>
                {(item.activities || []).length > 0 && !item.done && (
                  <div className="flex flex-wrap gap-1 mt-1 pl-6" data-testid={`mission-chain-${item.ref}`}>
                    {item.activities.map(a => (
                      <button key={a.key} onClick={() => navigate(a.route)}
                        data-testid={`mission-activity-${item.ref}-${a.key}`}
                        className="text-[10px] font-body px-2 py-0.5 rounded-full bg-white/80 border border-slate-200 text-slate-600 hover:border-indigo-300">
                        {a.label}
                      </button>
                    ))}
                    {onStart && item.node_type !== 'puzzle' && item.node_type !== 'mock' && (
                      <button onClick={() => onStart(item.subject, (item.label || '').replace(/^Revise: |^Full-portion sweep: /, ''))}
                        data-testid={`mission-start-session-${item.ref}`}
                        className="text-[10px] font-heading px-2 py-0.5 rounded-full bg-indigo-600 text-white inline-flex items-center gap-1">
                        <Play className="w-2.5 h-2.5" /> Start study session
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-3 flex items-center gap-3">
        <button data-testid="mission-open-planner" onClick={() => navigate('/planner')}
          className="text-[11px] font-heading text-indigo-600 hover:underline">
          Open the full plan →
        </button>
        <button data-testid="mission-open-cockpit" onClick={() => navigate('/cockpit')}
          className="text-[11px] font-heading text-fuchsia-600 hover:underline">
          Open today's cockpit →
        </button>
      </div>
    </div>
  );
}

export default TodaysMissionCard;
